import { useMemo, useState } from 'react';

const getParam = ( key ) => {
    const value = new URLSearchParams( window.location.search ).get( key );
    return value === null ? '' : value;
};

const setParam = ( key, value ) => {
    if ( !value ) {
        const urlParams = new URLSearchParams( window.location.search );
        urlParams.delete( key );
        window.history.replaceState( {}, '', 
            `${ window.location.pathname }?${ urlParams.toString() }` );
    } else {
        const urlParams = new URLSearchParams( window.location.search );
        urlParams.set( key, value );
        window.history.replaceState( { key: value }, '', 
            `${ window.location.pathname }?${ urlParams.toString() }` );
    }
};

const useURLPageQuery = () => {
    const [ value, setValue ] = useState( parseInt( getParam( 'page' ) ) || 1 );

    const updateCurrentPage = ( pageNumber ) => {
        const i = parseInt( pageNumber );
        if ( isNaN( i ) ) { return; }
        setValue( i );
        setParam( 'page', pageNumber ); 
    };
         
    return useMemo( () => {
        return {
            currentPage: value,
            setCurrentPage: updateCurrentPage
        };
    }, [ value ] );
};

const useURLSearchQuery = () => {
    const [ value, setValue ] = useState( getParam( 'search' ) );
    const [ searchWords, setSearchWords ] = useState( getParam( 'search' ).split( ' ' ) );

    const updateSearchText = ( text ) => {
        setValue( text );
        setParam( 'search', text );
        setSearchWords( text.split( ' ' ) );
    };
         
    return useMemo( () => {
        return {
            searchText: value,
            setSearchText: updateSearchText,
            searchWords
        };
    }, [ value, searchWords ] );
};

const useURLSortFieldQuery = () => {
    const [ value, setValue ] = useState( getParam( 'sort' ) );
   
    const updateSortField = ( text ) => {
        setValue( text || '' );
        setParam( 'sort', text || '' ); 
    };
         
    return useMemo( () => {
        return {
            sortField: value,
            setSortField: updateSortField
        };
    }, [ value ] );
};

const useURLSortDirectionQuery = () => {
    const [ value, setValue ] = useState( getParam( 'dir' ) );
   
    const updateSortField = ( text ) => {
        setValue( text || '' );
        setParam( 'dir', text || '' ); 
    };
         
    return useMemo( () => {
        return {
            sortDirection: value,
            setSortDirection: updateSortField
        };
    }, [ value ] );
};

const useURLFiltersQuery = () => {
    const [ value, setValue ] = useState( getParam( 'filters' ) );
   
    const updateFiltersField = ( filters ) => {
        setValue( filters || '' );
        setParam( 'filters', filters || '' ); 
    };
         
    return useMemo( () => {
        return {
            filters: value,
            setFilters: updateFiltersField
        };
    }, [ value ] );
};

const useURLBrandsFilterQuery = () => {
    const [ value, setValue ] = useState( getParam( 'brands' ) );
   
    const updateBrandsField = ( brands ) => {
        setValue( brands || '' );
        setParam( 'brands', brands || '' ); 
    };
         
    return useMemo( () => {
        return {
            brandsFilter: value,
            setBrandsFilter: updateBrandsField
        };
    }, [ value ] );
};

const useURLQueries = () => {
    const { currentPage, setCurrentPage } = useURLPageQuery();
    const { searchText, setSearchText, searchWords } = useURLSearchQuery();
    const { sortField, setSortField } = useURLSortFieldQuery();
    const { sortDirection, setSortDirection } = useURLSortDirectionQuery();
    const { filters, setFilters  } = useURLFiltersQuery();
    const { brandsFilter, setBrandsFilter } = useURLBrandsFilterQuery();

    return useMemo( () => {
        return {
            currentPage, setCurrentPage, searchText, setSearchText, searchWords,
            sortField, setSortField, sortDirection, setSortDirection, filters, 
            setFilters, brandsFilter, setBrandsFilter
        }; 
    }, [ currentPage, setCurrentPage, searchText, setSearchText, searchWords, 
        sortField, setSortField, sortDirection, setSortDirection, filters, 
        setFilters, brandsFilter, setBrandsFilter ] );
};

export { 
    useURLPageQuery, 
    useURLSearchQuery, 
    useURLSortFieldQuery,
    useURLSortDirectionQuery,
    useURLQueries,
    setParam, getParam,
};
